/**
 * Tiered Content Component
 *
 * Handles progressive disclosure based on access levels.
 * Shows blurred overlay with access request for gated content.
 */

import { ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'

export type AccessTier = 'public' | 'registered' | 'approved'

export interface TieredContentProps {
  /** Content to display */
  children: ReactNode
  /** Required access tier to view full content */
  requiredTier: AccessTier
  /** Public preview content (shown when access is insufficient) */
  publicPreview?: ReactNode
  /** Custom message for access request */
  accessMessage?: string
  /** Callback when user requests access */
  onRequestAccess?: () => void
  /** Override access check (for testing) */
  hasAccess?: boolean
  className?: string
}

/**
 * Determine if user has sufficient access based on tier
 */
function checkAccess(
  requiredTier: AccessTier,
  isAuthenticated: boolean,
  userAccessLevel?: string
): boolean {
  switch (requiredTier) {
    case 'public':
      return true
    case 'registered':
      return isAuthenticated
    case 'approved':
      return isAuthenticated && userAccessLevel === 'approved'
    default:
      return false
  }
}

export function TieredContent({
  children,
  requiredTier,
  publicPreview,
  accessMessage = "I validate interest for privacy & security. Once approved, you'll unlock this content and more.",
  onRequestAccess,
  hasAccess: hasAccessOverride,
  className = '',
}: TieredContentProps) {
  const { isAuthenticated, user } = useAuth()

  // Determine access
  const hasAccess = hasAccessOverride ?? checkAccess(
    requiredTier,
    isAuthenticated,
    user?.platformRole // Use platformRole as proxy for access level
  )

  // Full access - render children
  if (hasAccess) {
    return <>{children}</>
  }

  // Gated - show preview with overlay
  return (
    <div className={`mac-studio-gated ${className}`}>
      {/* Preview content (blurred) */}
      {publicPreview && (
        <div style={{ filter: 'blur(4px)', pointerEvents: 'none' }}>
          {publicPreview}
        </div>
      )}

      {/* Access overlay */}
      <div className="mac-studio-gated__overlay">
        <span className="mac-studio-gated__icon">🔐</span>
        <h3 className="mac-studio-gated__title">Request Access</h3>
        <p className="mac-studio-gated__message">{accessMessage}</p>
        <button
          type="button"
          className="mac-studio-gated__button"
          onClick={onRequestAccess}
        >
          Request Access
        </button>
      </div>
    </div>
  )
}

/**
 * Access Badge Component
 *
 * Shows user's current access level.
 */
export interface AccessBadgeProps {
  tier: AccessTier
}

export function AccessBadge({ tier }: AccessBadgeProps) {
  const labels: Record<AccessTier, string> = {
    public: 'Public',
    registered: 'Registered',
    approved: '✓ Full Access',
  }

  return (
    <span className="mac-studio-access-badge">
      {labels[tier]}
    </span>
  )
}
